"use client";

import { useReportWebVitals } from "next/web-vitals";
import posthog from "posthog-js";

import { PRODUCT_DEF } from "@/lib/product";

import type { SignupProduct } from "@/lib/product";

// next/web-vitals re-exports `Metric` from `next/dist/compiled/web-vitals`,
// which doesn't resolve cleanly through the ESLint type checker. Re-declare
// the subset we read here to keep the call site type-safe.
interface WebVitalMetric {
  name: string;
  value: number;
  delta: number;
  id: string;
  rating: "good" | "needs-improvement" | "poor";
  navigationType:
    | "navigate"
    | "reload"
    | "back-forward"
    | "back-forward-cache"
    | "prerender"
    | "restore";
}

interface WebVitalContext {
  appVersion: string;
  pathname: string;
  platform: "web";
  product: SignupProduct;
  viewportWidth: number;
}

export function classifyRoute(pathname: string): string {
  const clean = pathname.split("?", 1)[0] ?? pathname;
  return clean.split("/").find(Boolean) ?? "home";
}

function classifyViewport(
  width: number,
): "desktop" | "narrow" | "phone" | "tablet" {
  if (width < 360) return "narrow";
  if (width < 600) return "phone";
  if (width < 1024) return "tablet";
  return "desktop";
}

export function buildWebVitalProperties(
  metric: WebVitalMetric,
  context: WebVitalContext,
): Record<string, unknown> {
  return {
    [`$web_vitals_${metric.name}_value`]: metric.value,
    [`$web_vitals_${metric.name}_event`]: {
      name: metric.name,
      value: metric.value,
      delta: metric.delta,
      id: metric.id,
      rating: metric.rating,
      navigationType: metric.navigationType,
    },
    app_version: context.appVersion,
    platform: context.platform,
    product: context.product,
    route_class: classifyRoute(context.pathname),
    viewport_class: classifyViewport(context.viewportWidth),
  };
}

// Explicit dispatch of Core Web Vitals to PostHog so it doesn't depend on
// the PostHog project's autocapture_web_vitals_opt_in server setting being on.
// PostHog expects a single $web_vitals event with per-metric namespaced
// properties (`$web_vitals_<METRIC>_value` + `$web_vitals_<METRIC>_event`).
// Next's hook fires once per metric per navigation; PostHog server-side
// aggregates them into the Web Vitals dashboard.
export function WebVitalsTracker() {
  useReportWebVitals((metric: WebVitalMetric) => {
    posthog.capture(
      "$web_vitals",
      buildWebVitalProperties(metric, {
        appVersion: process.env.NEXT_PUBLIC_APP_VERSION ?? "unknown",
        pathname:
          typeof window === "undefined" ? "/" : window.location.pathname,
        platform: "web",
        product: PRODUCT_DEF.signupProduct,
        viewportWidth:
          typeof window === "undefined" ? 1_440 : window.innerWidth,
      }),
    );
  });

  return null;
}
